import classnames from 'classnames';
import React from 'react';
import { snakeCaseToTitleCase } from '~/lib/cases';
import { isNumber } from '~/lib/math';
import { isDecPageField } from '~/utils/dec_page_fields';
import { useDocumentView } from '../../providers';
const areCoordinatesValid = (coordinates) => {
    if (!coordinates)
        return false;
    const { x1, x2, y1, y2 } = coordinates;
    return (isNumber(x1) && isNumber(x2) && isNumber(y1) && isNumber(y2));
};
const DecPageField = ({ k, v }) => {
    const { setHighlights } = useDocumentView();
    const navigateToCoordinates = (pageNumber, coordinates) => {
        const { x1, x2, y1, y2 } = coordinates;
        if (x1 === 0 && x2 === 0 && y1 === 0 && y2 === 0) {
            coordinates.x1 = 0.5;
            coordinates.x2 = 0.5;
            coordinates.y1 = 0.5;
            coordinates.y2 = 0.5;
        }
        setHighlights([{ pageNum: pageNumber, coordinates: coordinates }]);
    };
    const getValueFormatted = (value) => {
        if (value === null || value === undefined)
            return renderPrimitive(value);
        if (typeof value === 'string' || typeof value === 'number')
            return renderPrimitive(value);
        if (isDecPageField(value)) {
            return renderPrimitive(value.value, value.page_number, value.coordinates);
        }
        if (Array.isArray(value))
            return React.createElement(React.Fragment, null,
                React.createElement("b", { className: 'key' }, snakeCaseToTitleCase(k)),
                React.createElement("div", { className: 'value' }, recursiveArrayPrint(value)));
        if (typeof value === 'object')
            return React.createElement(React.Fragment, null,
                React.createElement("b", { className: 'key' }, snakeCaseToTitleCase(k)),
                React.createElement("div", { className: 'value' }, recursiveObjectPrint(value)));
        return React.createElement("pre", null, JSON.stringify(value, null, 2));
    };
    const recursiveArrayPrint = (value) => {
        return React.createElement(React.Fragment, null, value.map(item => {
            var _a;
            if (item === undefined || item === null)
                return React.createElement(React.Fragment, null);
            if (typeof item === 'string' || typeof item === 'number')
                return React.createElement("div", null, item);
            if (isDecPageField(item)) {
                const { page_number, coordinates } = value;
                const hasNavigation = (page_number && coordinates && isNumber(page_number) && areCoordinatesValid(coordinates));
                const classNames = classnames('', { navigable: hasNavigation });
                return React.createElement("div", { className: classNames, onClick: () => {
                        if (!hasNavigation)
                            return;
                        navigateToCoordinates(Number(page_number), coordinates);
                    }, style: { flex: '1' } }, (_a = value.value) !== null && _a !== void 0 ? _a : '');
            }
            if (Array.isArray(item))
                return recursiveArrayPrint(item);
            if (typeof item === 'object')
                return recursiveObjectPrint(item);
            return React.createElement("pre", null, JSON.stringify(item, null, 2));
        }));
    };
    const recursiveObjectPrint = (value) => {
        if (!value)
            return React.createElement(React.Fragment, null);
        return React.createElement(React.Fragment, null, Object.keys(value).map(key => {
            var _a;
            const newValue = value[key];
            const hiddenTitles = ['policy_check', 'citation'];
            const title = hiddenTitles.includes(key)
                ? React.createElement(React.Fragment, null)
                : React.createElement(React.Fragment, null,
                    React.createElement("b", null, snakeCaseToTitleCase(key)),
                    ":\u00A0");
            const indent = React.createElement("div", { style: { width: '20px' } });
            if (typeof newValue === 'string' || typeof newValue === 'number') {
                if (key === 'forensic')
                    return React.createElement(React.Fragment, null);
                return React.createElement("div", { style: { display: 'flex' } },
                    indent,
                    React.createElement("div", { style: { flex: '1' } },
                        title,
                        newValue));
            }
            if (isDecPageField(value[key])) {
                const { page_number, coordinates } = value[key];
                const hasNavigation = (page_number && coordinates && isNumber(page_number) && areCoordinatesValid(coordinates));
                const classNames = classnames('', { navigable: hasNavigation });
                return React.createElement("div", { className: classNames, onClick: () => {
                        if (!hasNavigation)
                            return;
                        navigateToCoordinates(Number(page_number), coordinates);
                    }, style: { flex: '1' } },
                    indent,
                    React.createElement("div", null,
                        title, (_a = value[key].value) !== null && _a !== void 0 ? _a : ''));
            }
            if (Array.isArray(newValue)) {
                return React.createElement("div", { style: { display: 'flex' } },
                    indent,
                    React.createElement("div", { style: { flex: '1' } },
                        title,
                        recursiveArrayPrint(newValue)));
            }
            if (typeof newValue === 'object' && !Array.isArray(newValue)) {
                return React.createElement("div", null,
                    title,
                    React.createElement("div", { style: { display: 'flex' } },
                        indent,
                        React.createElement("div", { style: { flex: '1' } }, recursiveObjectPrint(newValue))));
            }
            return React.createElement("pre", null, JSON.stringify(newValue, null, 2));
        }));
    };
    const renderPrimitive = (value, pageNumber, coordinates) => {
        const hasNavigation = (pageNumber && coordinates && isNumber(pageNumber) && areCoordinatesValid(coordinates));
        const classNames = classnames('', { navigable: hasNavigation });
        return React.createElement("div", { className: classNames, onClick: () => {
                if (!hasNavigation)
                    return;
                navigateToCoordinates(Number(pageNumber), coordinates);
            }, style: { flex: '1' } },
            React.createElement("b", { className: 'key' }, snakeCaseToTitleCase(k)),
            React.createElement("div", { className: 'value' }, value !== null && value !== void 0 ? value : ''));
    };
    return getValueFormatted(v);
};
export default DecPageField;
